import { type FormEvent, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/store/useAppStore";
import { healthcheckUrl } from "@/client/colyseusEndpoint";
import { createRoom, getRoom, leaveSession } from "@/client/session";

export const Route = createFileRoute("/")({
  component: Index,
});

function Index() {
  const storedPseudo = useAppStore((state) => state.pseudo);
  const setPseudo = useAppStore((state) => state.setPseudo);
  const navigate = useNavigate();
  const [pseudoInput, setPseudoInput] = useState(storedPseudo);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedPseudo = pseudoInput.trim();

  useEffect(() => {
    // L'accueil, c'est « je ne suis dans aucune Room ». Y revenir par le bouton
    // Retour du navigateur doit donc libérer le siège, sinon on laisse un
    // fantôme dans le lobby qu'on vient de quitter.
    if (getRoom() != undefined) void leaveSession();
  }, []);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!trimmedPseudo || creating) return;

    setPseudo(trimmedPseudo);
    setCreating(true);
    setError(null);

    try {
      // Le create vit ici, pas derrière une URL : une route qui crée fabrique
      // une Room de plus à chaque F5 et à chaque retour arrière. On ne navigue
      // qu'une fois le roomId en main.
      const room = await createRoom(trimmedPseudo);
      navigate({ to: "/lobby/$roomId", params: { roomId: room.roomId } });
    } catch (err) {
      console.warn("[session] création impossible", err);
      setError("Impossible de créer la partie. Le serveur répond-il ?");
      setCreating(false);
    }
  }

  function handleJoin() {
    if (!trimmedPseudo) return;
    setPseudo(trimmedPseudo);
    navigate({ to: "/join" });
  }

  return (
    <main className="mx-auto flex min-h-svh max-w-md flex-col items-center justify-center gap-8 px-4 text-center">
      <h1 className="font-heading text-4xl font-bold tracking-tight">Ninja Survivors</h1>

      <form onSubmit={handleCreate} className="flex w-full flex-col gap-4">
        <Input
          value={pseudoInput}
          onChange={(event) => setPseudoInput(event.target.value)}
          placeholder="Pseudo"
          aria-label="Pseudo"
          maxLength={16}
          autoFocus
        />

        <Button type="submit" disabled={!trimmedPseudo || creating}>
          {creating ? "Création..." : "Nouvelle partie"}
        </Button>

        <Button type="button" variant="outline" disabled={!trimmedPseudo} onClick={handleJoin}>
          Rejoindre une partie
        </Button>
      </form>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <StatusPill />
    </main>
  );
}

type ConnectionStatus = "connecting" | "connected" | "disconnected";

function StatusPill() {
  const [status, setStatus] = useState<ConnectionStatus>("connecting");

  useEffect(() => {
    // Un simple GET : rejoindre une room pour tester la disponibilité créait un
    // joueur dans le World (onJoin), et on se retrouvait à deux une fois arrivé
    // sur /play. Le lobby observe, il ne joue pas.
    const controller = new AbortController();

    fetch(healthcheckUrl(), { signal: controller.signal })
      .then((response) => setStatus(response.ok ? "connected" : "disconnected"))
      .catch(() => {
        // L'abort du démontage n'est pas une panne du back.
        if (!controller.signal.aborted) setStatus("disconnected");
      });

    return () => controller.abort();
  }, []);

  const shortSha = import.meta.env.VITE_APP_VERSION?.slice(0, 7) || "dev";

  return (
    <div className="fixed bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border bg-background/80 px-3 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          status === "connecting" && "bg-muted-foreground/40",
          status === "connected" && "bg-green-500",
          status === "disconnected" && "bg-destructive",
        )}
        aria-hidden
      />
      <span>{shortSha}</span>
      <span className="text-muted-foreground/40" aria-hidden>
        ·
      </span>
      <span>
        {status === "connecting" && "connexion..."}
        {status === "connected" && "connecté"}
        {status === "disconnected" && "déconnecté"}
      </span>
    </div>
  );
}
